import { useEffect, useMemo, useState } from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import Heading from '@zenbusiness/zui/Heading';
import LinearProgress from '@zenbusiness/zui/LinearProgress';
import LoadingButton from '@zenbusiness/zui/LoadingButton';
import Radio from '@zenbusiness/zui/Radio';
import RadioGroup from '@zenbusiness/zui/RadioGroup';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';

import { FocusGrid, ContainerGrid, ActionTwoButtonsGrid } from '#client/components/Grid';
import { CARD_STATUS, CARD_TYPE } from '#client/constants/cards';
import useCardsContext from '#client/hooks/useCardsContext';
import useEnterSubmit from '#client/hooks/useEnterSubmit';
import useFormStep from '#client/hooks/useFormStep';
import { getDiffTime } from '#client/utils/date';

import {
  BoxContentStyled,
  DialogContentStyled,
  FormControlLabelStyled,
  Wrapper
} from './CustomModal.styles';

import { REASON_OF_REQUESTING, SUMMARY, SENDING_ADDRESS, NO_VALUE_CARD } from '../constants/card';

export default function ChooseCardFrom({ onClose, applicationLoading }) {
  const { setCurrentFormStep, data, setData } = useFormStep();
  const nextButtonId = useEnterSubmit();
  const { physicalCard, virtualCard, notHasPhysicalCard, cardsLoading } = useCardsContext();
  const diffDay = Math.abs(getDiffTime(physicalCard?.updatedAt, 'h'));
  const isSuspectedFraudCard = physicalCard?.status === CARD_STATUS.SUSPECTED_FRAUD;
  const isInActivePhysicalCard = physicalCard?.status === CARD_STATUS.INACTIVE;
  const isCancelBySuspectedFraud = isSuspectedFraudCard && diffDay >= 24;

  const cardOptions = useMemo(() => {
    const listCardOptions = [];
    // If we have virtual card, show virtual card option
    if (virtualCard) {
      listCardOptions.push({
        label: 'Virtual debit card',
        value: virtualCard.id,
        type: CARD_TYPE.BUSINESS_VIRTUAL_DEBIT_CARD,
        status: virtualCard.status,
        updatedAt: virtualCard.updatedAt
      });
    }

    // If we do not have physical card, show physical card option with null value
    if (notHasPhysicalCard) {
      listCardOptions.push({
        label: 'Physical debit card',
        value: NO_VALUE_CARD.PHYSICAL_CARD,
        type: CARD_TYPE.BUSINESS_DEBIT_CARD,
        status: null,
        updatedAt: null
      });
    }

    // There are 3 case to add normal physical card option:
    // 1. Have physical card and card status is not SUSPECTED_FRAUD
    // 2. Have physical card and card status is not INACTIVE
    // 3. Physical card have SUSPECTED_FRAUD status more than 24 hour
    if (
      (physicalCard && !isSuspectedFraudCard && !isInActivePhysicalCard) ||
      isCancelBySuspectedFraud
    ) {
      listCardOptions.push({
        label: 'Physical debit card',
        value: physicalCard.id,
        type: CARD_TYPE.BUSINESS_DEBIT_CARD,
        status: physicalCard.status,
        updatedAt: physicalCard.updatedAt
      });
    }

    return listCardOptions;
  }, [
    virtualCard,
    notHasPhysicalCard,
    physicalCard,
    isSuspectedFraudCard,
    isInActivePhysicalCard,
    isCancelBySuspectedFraud
  ]);

  const [cardTypeSelected, setCardTypeSelected] = useState(data.cardId);

  const buttonPressedTracker = useButtonPressedCallback({
    elementName: 'replace_card',
    elementText: 'Next',
    appPageName: 'replace_card_modal'
  });

  const handleSubmit = () => {
    buttonPressedTracker();
    const option = cardOptions.find((cardOption) => cardOption.value === cardTypeSelected) || {};

    setData({
      ...data,
      cardType: option.type,
      cardId: option.value,
      cardStatus: option.status
    });

    // If user chooses virtual card, go to summary screen and replace it
    if (option.type === CARD_TYPE.BUSINESS_VIRTUAL_DEBIT_CARD) {
      setCurrentFormStep(SUMMARY);
      return;
    }

    // If user chooses physical and it has value, go to reason screen
    if (
      option.type === CARD_TYPE.BUSINESS_DEBIT_CARD &&
      option.value !== NO_VALUE_CARD.PHYSICAL_CARD
    ) {
      setCurrentFormStep(REASON_OF_REQUESTING);
      return;
    }

    // If user chooses physical and it does not have value, go to address screen to create new physical card
    setCurrentFormStep(SENDING_ADDRESS);
  };

  const handleChange = (e, value) => {
    setCardTypeSelected(value);
  };

  const defaultCardID = cardOptions[0]?.value;

  useEffect(() => {
    if (!cardsLoading && defaultCardID && !data.cardId) {
      setCardTypeSelected(defaultCardID);
    }
  }, [defaultCardID, data.cardId, cardsLoading]);

  return (
    <>
      <DialogContentStyled>
        <ContainerGrid>
          <FocusGrid>
            <Wrapper spacing="32px">
              <Heading
                family="secondary"
                size="3xl"
                weight="bold"
              >
                What type of card do you need?
              </Heading>
              {cardsLoading ? (
                <LinearProgress />
              ) : (
                <BoxContentStyled>
                  <RadioGroup
                    aria-label="Choose card type."
                    name="card-type-radio-buttons-group"
                    onChange={handleChange}
                    sx={{
                      gap: '32px'
                    }}
                    value={cardTypeSelected}
                  >
                    {cardOptions.map(({ value, label }) => (
                      <FormControlLabelStyled
                        control={<Radio />}
                        key={value}
                        label={
                          <Text
                            color="primary"
                            size="xl"
                            weight="medium"
                          >
                            {label}
                          </Text>
                        }
                        value={value}
                      />
                    ))}
                  </RadioGroup>
                </BoxContentStyled>
              )}
            </Wrapper>
          </FocusGrid>
        </ContainerGrid>
      </DialogContentStyled>
      <ActionTwoButtonsGrid
        firstButton={
          <LoadingButton
            fullWidth
            onClick={onClose}
            size="large"
            variant="outlined"
          >
            Never mind
          </LoadingButton>
        }
        secondButton={
          <LoadingButton
            color="primary"
            disabled={!cardTypeSelected || applicationLoading}
            fullWidth
            id={nextButtonId}
            onClick={handleSubmit}
            size="large"
            variant="contained"
          >
            Next
          </LoadingButton>
        }
      />
    </>
  );
}

ChooseCardFrom.propTypes = {
  applicationLoading: PropTypes.bool,
  onClose: PropTypes.func.isRequired
};
